import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import ClearIcon from '@mui/icons-material/Clear'
import EditIcon from '@mui/icons-material/Edit'
import LinkIcon from '@mui/icons-material/Link'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import VideocamIcon from '@mui/icons-material/Videocam'
import VisibilityIcon from '@mui/icons-material/Visibility'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker'
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import type { DateRange } from '@mui/x-date-pickers-pro'
import type { Dayjs } from 'dayjs'
import TablePagination from 'src/v3/components/table-pagination'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useDebouncedValue } from '../hooks/use-debounced-value'
import {
  activeEventTabSet,
  activeSessionSet,
  attendeeStatusPresetSet,
  fetchManageEventInstructors,
  fetchManageEventSessions,
  selectManageEventById,
  selectManageEventInstructorMap,
  selectManageEventInstructorsByLinkedService,
  selectManageEventInstructorsStatus,
  selectManageEventSessions,
  selectManageEventSessionsStatus,
  selectManageEventSessionsTotal,
  type ManageEvent,
} from '../manage-events.slice'
import type { EventSessionItem, SessionOption } from '../types'
import { EVENT_COLOR_VIRTUAL, EVENT_COLOR_IN_PERSON } from '../utils/constants'
import { formatSessionMeta } from '../utils/session-time'
import RsvpStatCards, { buildRsvpStatCols } from '../components/rsvp-stat-cards'
import InviteUsersDialog from '../components/invite-users-dialog'

dayjs.extend(utc)
dayjs.extend(timezone)

type Props = { eventID: string }

let STATUS_OPTIONS = [
  { value: 'registration-open', label: 'Registration open' },
  { value: 'registration-closed', label: 'Registration closed' },
  { value: 'past', label: 'Past sessions' },
]

type SessionStatus = 'Registration open' | 'Registration closed' | 'Past'

let deriveSessionStatus = (session: EventSessionItem): SessionStatus => {
  let now = dayjs.utc()
  if (dayjs.utc(session.sessionTime).isBefore(now)) return 'Past'
  if (
    session.registrationDeadline &&
    dayjs.utc(session.registrationDeadline).isBefore(now)
  )
    return 'Registration closed'
  return 'Registration open'
}

let sessionStatusColor = (status: SessionStatus) =>
  status === 'Registration open'
    ? 'success.main'
    : status === 'Registration closed'
      ? 'info.main'
      : undefined

let deriveEventLocation = (
  event: ManageEvent,
): { isVirtual: boolean; locationLabel: string } => {
  let et = event.eventType
  if ('virtual' in et)
    return { isVirtual: true, locationLabel: et.virtual.url || et.virtual.name }
  if ('inPerson' in et)
    return { isVirtual: false, locationLabel: et.inPerson.name }
  if ('hybrid' in et)
    return { isVirtual: false, locationLabel: et.hybrid[0].name }
  return { isVirtual: false, locationLabel: '' }
}

// ─── Session Card ─────────────────────────────────────────────────────────────

type SessionCardProps = {
  session: EventSessionItem
  isVirtual: boolean
  locationLabel: string
  instructorLine: string
  onViewAttendees: (session: EventSessionItem, statusKey?: string) => void
  onEdit: () => void
  // Omitted for FOAMfrat events, which the bulk invite routes reject.
  onInvite?: (session: EventSessionItem) => void
}

let SessionCard = memo(
  ({
    session,
    isVirtual,
    locationLabel,
    instructorLine,
    onViewAttendees,
    onEdit,
    onInvite,
  }: SessionCardProps) => {
    let theme = useTheme()

    let badgeColor = isVirtual ? EVENT_COLOR_VIRTUAL : EVENT_COLOR_IN_PERSON
    let sessionName = session.name ?? 'Session'
    let meta = formatSessionMeta(session.sessionTime, session.timeZone)
    let status = deriveSessionStatus(session)

    let rsvpCols = buildRsvpStatCols(theme, {
      pending: session.capacitySummary?.totalPending ?? 0,
      accepted: session.capacitySummary?.totalAccepted ?? 0,
      waitlisted: session.capacitySummary?.totalWaitlisted ?? 0,
      declined: session.capacitySummary?.totalDeclined ?? 0,
      attended: session.capacitySummary?.totalAttended ?? 0,
    })

    return (
      <Box
        className="rounded p-4"
        sx={{
          background: '#1e2124',
          border: '1px solid rgba(255,255,255,.12)',
        }}
      >
        <Box
          className="flex"
          sx={{
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 3 },
            alignItems: { xs: 'stretch', md: 'center' },
          }}
        >
          {/* Left: badge + text info */}
          <Box className="flex items-start gap-4 flex-1 min-w-0">
            <Box
              className="flex items-center justify-center flex-none rounded-full"
              sx={{
                width: 42,
                height: 42,
                background: `color-mix(in srgb, ${badgeColor} 16%, transparent)`,
                color: badgeColor,
              }}
            >
              {isVirtual ? (
                <VideocamIcon sx={{ fontSize: 22 }} />
              ) : (
                <LocationOnIcon sx={{ fontSize: 22 }} />
              )}
            </Box>
            <Box className="flex-1 min-w-0">
              <Typography sx={{ fontWeight: 500 }}>{sessionName}</Typography>
              <Typography
                className="mt-0.5"
                sx={{ fontSize: 13, color: 'rgba(255,255,255,.7)' }}
              >
                {meta}
              </Typography>
              <Box className="flex items-center gap-2 mt-1">
                {isVirtual ? (
                  <LinkIcon
                    className="shrink-0"
                    sx={{ fontSize: 16, color: 'rgba(255,255,255,.4)' }}
                  />
                ) : (
                  <LocationOnIcon
                    className="shrink-0"
                    sx={{ fontSize: 16, color: 'rgba(255,255,255,.4)' }}
                  />
                )}
                <Typography
                  className="overflow-hidden text-ellipsis whitespace-nowrap"
                  sx={{ fontSize: 13, color: 'rgba(255,255,255,.55)' }}
                >
                  {locationLabel}
                </Typography>
              </Box>
              {instructorLine && (
                <Typography
                  className="mt-0.5"
                  sx={{ fontSize: 13, color: 'rgba(255,255,255,.55)' }}
                >
                  {instructorLine}
                </Typography>
              )}
              <Typography
                className="mt-1"
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: sessionStatusColor(status) ?? 'rgba(255,255,255,.55)',
                }}
              >
                {status === 'Past' ? 'Past session' : status}
              </Typography>
            </Box>
          </Box>

          {/* Right: RSVP stats + action buttons */}
          <Box
            className="flex"
            sx={{
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 2, md: 3 },
              alignItems: { xs: 'stretch', md: 'center' },
            }}
          >
            {/* RSVP stats */}
            <Box className="flex flex-wrap gap-4">
              {rsvpCols.map((col) => (
                <Box
                  key={col.label}
                  component="button"
                  type="button"
                  onClick={() => onViewAttendees(session, col.statusKey)}
                  title={`View ${col.label}`}
                  className="p-0 text-center bg-transparent border-none cursor-pointer flex-none"
                  sx={{
                    fontFamily: 'inherit',
                    '&:hover': { opacity: 0.75 },
                    '&:focus-visible': {
                      outline: '2px solid currentColor',
                      outlineOffset: 2,
                      borderRadius: 1,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 500,
                      color: col.color,
                      lineHeight: 1,
                    }}
                  >
                    {col.value}
                  </Typography>
                  <Typography
                    className="mt-0.5 uppercase"
                    sx={{
                      fontSize: 11,
                      color: 'rgba(255,255,255,.55)',
                      letterSpacing: '0.05em',
                      lineHeight: 1,
                    }}
                  >
                    {col.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Action buttons */}
            <Box
              className="flex gap-1"
              sx={{ alignSelf: { xs: 'flex-end', md: 'auto' } }}
            >
              {onInvite && (
                <Tooltip title={status === 'Past' ? 'Session has started' : ''}>
                  {/* span so the tooltip still fires while the button is disabled */}
                  <span className="flex">
                    <Box
                      component="button"
                      type="button"
                      onClick={() => onInvite(session)}
                      disabled={status === 'Past'}
                      title={status === 'Past' ? undefined : 'Invite users'}
                      aria-label="Invite users"
                      className="flex items-center justify-center bg-transparent border-none rounded cursor-pointer disabled:cursor-default disabled:opacity-40"
                      sx={{
                        width: 36,
                        height: 36,
                        color: EVENT_COLOR_VIRTUAL,
                        '&:hover:not(:disabled)': {
                          bgcolor: 'rgba(255,255,255,.08)',
                          color: '#fff',
                        },
                        '&:focus-visible': {
                          outline: '2px solid',
                          outlineColor: EVENT_COLOR_VIRTUAL,
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <PersonAddIcon sx={{ fontSize: 20 }} />
                    </Box>
                  </span>
                </Tooltip>
              )}
              <Box
                component="button"
                type="button"
                onClick={() => onViewAttendees(session)}
                title="View attendees"
                aria-label="View attendees"
                className="flex items-center justify-center bg-transparent border-none rounded cursor-pointer"
                sx={{
                  width: 36,
                  height: 36,
                  color: EVENT_COLOR_VIRTUAL,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,.08)',
                    color: '#fff',
                  },
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: EVENT_COLOR_VIRTUAL,
                    outlineOffset: 2,
                  },
                }}
              >
                <VisibilityIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box
                component="button"
                type="button"
                onClick={onEdit}
                title="Edit event"
                aria-label="Edit event"
                className="flex items-center justify-center bg-transparent border-none rounded cursor-pointer"
                sx={{
                  width: 36,
                  height: 36,
                  color: EVENT_COLOR_VIRTUAL,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,.08)',
                    color: '#fff',
                  },
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: EVENT_COLOR_VIRTUAL,
                    outlineOffset: 2,
                  },
                }}
              >
                <EditIcon sx={{ fontSize: 20 }} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    )
  },
)

// ─── Sessions Tab ─────────────────────────────────────────────────────────────

let SessionsTab = ({ eventID }: Props) => {
  let [search, setSearch] = useState('')
  let [instructorFilter, setInstructorFilter] = useState('')
  let [statusFilter, setStatusFilter] = useState('')
  let [dateFrom, setDateFrom] = useState<Dayjs | null>(null)
  let [dateTo, setDateTo] = useState<Dayjs | null>(null)
  let [page, setPage] = useState(1)
  let [pageSize, setPageSize] = useState(10)

  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let event = useAppSelector(selectManageEventById(eventID))
  let instructorMap = useAppSelector(selectManageEventInstructorMap)
  let instructorsStatus = useAppSelector(selectManageEventInstructorsStatus)
  useAutoFetch(instructorsStatus, fetchManageEventInstructors)

  let sessions = useAppSelector(selectManageEventSessions)
  let sessionsStatus = useAppSelector(selectManageEventSessionsStatus)
  let total = useAppSelector(selectManageEventSessionsTotal)
  let rsvpCounts = useMemo(
    () =>
      sessions.reduce(
        (acc, s) => ({
          pending: acc.pending + (s.capacitySummary?.totalPending ?? 0),
          accepted: acc.accepted + (s.capacitySummary?.totalAccepted ?? 0),
          waitlisted:
            acc.waitlisted + (s.capacitySummary?.totalWaitlisted ?? 0),
          declined: acc.declined + (s.capacitySummary?.totalDeclined ?? 0),
          attended: acc.attended + (s.capacitySummary?.totalAttended ?? 0),
        }),
        { pending: 0, accepted: 0, waitlisted: 0, declined: 0, attended: 0 },
      ),
    [sessions],
  )

  let selectEventInstructors = useMemo(
    () =>
      selectManageEventInstructorsByLinkedService(event?.linkedServiceID ?? ''),
    [event?.linkedServiceID],
  )
  let eventInstructors = useAppSelector(selectEventInstructors)

  let debouncedSearch = useDebouncedValue(search)

  let eventIDRef = useRef(eventID)
  // eslint-disable-next-line fp/no-mutation
  eventIDRef.current = eventID

  // Reset local filter state and fire the initial clean fetch when the event changes.
  // sessionsReset() is dispatched upstream in event-detail.tsx on eventID change.
  useEffect(() => {
    setPage(1)
    setPageSize(10)
    setSearch('')
    setInstructorFilter('')
    setStatusFilter('')
    setDateFrom(null)
    setDateTo(null)
    dispatch(fetchManageEventSessions({ eventID, page: 1, pageSize: 10 }))
  }, [eventID, dispatch])

  // Unified fetch: fires only on filter/pagination changes (not eventID changes).
  // eventID is read via ref so it's always current without being a dep.
  // Always resets to page 1 so we never send a stale offset to the API.
  useEffect(() => {
    setPage(1)
    dispatch(
      fetchManageEventSessions({
        eventID: eventIDRef.current,
        page: 1,
        pageSize,
        q: debouncedSearch || undefined,
        instructor: instructorFilter || undefined,
        status: statusFilter || undefined,
        dateFrom: dateFrom?.format('YYYY-MM-DD') ?? undefined,
        dateTo: dateTo?.format('YYYY-MM-DD') ?? undefined,
      }),
    )
  }, [
    debouncedSearch,
    instructorFilter,
    statusFilter,
    dateFrom,
    dateTo,
    pageSize,
    dispatch,
  ])

  let isLoading =
    sessionsStatus === 'uninitialized' || sessionsStatus === 'pending'

  let hasActiveFilters =
    !!search ||
    (eventInstructors.length > 1 && !!instructorFilter) ||
    !!statusFilter ||
    !!dateFrom ||
    !!dateTo

  let clearFilters = () => {
    setSearch('')
    setInstructorFilter('')
    setStatusFilter('')
    setDateFrom(null)
    setDateTo(null)
  }

  let goToAttendees = useCallback(
    (session: EventSessionItem, statusKey?: string) => {
      dispatch(
        activeSessionSet({
          id: session.id,
          title: session.name ?? 'Session',
          ts: session.sessionTime,
          timeZone: session.timeZone,
        }),
      )
      if (statusKey) dispatch(attendeeStatusPresetSet(statusKey))
      dispatch(activeEventTabSet(2))
    },
    [dispatch],
  )

  let [inviteSession, setInviteSession] = useState<SessionOption | null>(null)

  let handleInvite = useCallback(
    (session: EventSessionItem) =>
      setInviteSession({
        id: session.id,
        title: session.name ?? 'Session',
        ts: session.sessionTime,
        timeZone: session.timeZone,
      }),
    [],
  )

  // The view the manager is looking at when the invite save resolves, so the
  // card counts refresh without resetting their page or filters.
  let currentQuery = {
    eventID,
    page,
    pageSize,
    q: debouncedSearch || undefined,
    instructor: instructorFilter || undefined,
    status: statusFilter || undefined,
    dateFrom: dateFrom?.format('YYYY-MM-DD') ?? undefined,
    dateTo: dateTo?.format('YYYY-MM-DD') ?? undefined,
  }
  let currentQueryRef = useRef(currentQuery)
  // eslint-disable-next-line fp/no-mutation
  currentQueryRef.current = currentQuery

  // In the background so the cards stay mounted: the dialog returns focus to
  // the invite button that opened it, which a skeleton swap would detach.
  let handleInviteSaved = useCallback(() => {
    dispatch(
      fetchManageEventSessions({
        ...currentQueryRef.current,
        background: true,
      }),
    )
  }, [dispatch])

  let handleEdit = useCallback(
    () => navigate(`/events/${eventID}/edit`),
    [navigate, eventID],
  )

  let locationInfo = event
    ? deriveEventLocation(event)
    : { isVirtual: false, locationLabel: '' }

  let getInstructorLine = (session: EventSessionItem): string =>
    session.instructorIDs
      .map((id) => instructorMap[id]?.name ?? null)
      .filter(Boolean)
      .join(', ')

  return (
    <Box className="flex flex-col gap-4">
      <RsvpStatCards counts={rsvpCounts} />

      {/* Filter bar */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Stack direction="row" className="flex-wrap items-center gap-4">
          <TextField
            label="Search Sessions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1"
            sx={{ minWidth: 200, backgroundColor: 'background.paperDark' }}
          />
          <DateRangePicker
            value={[dateFrom, dateTo]}
            onChange={([from, to]: DateRange<Dayjs>) => {
              setDateFrom(from ?? null)
              setDateTo(to ?? null)
            }}
            label="Date range"
            slots={{ field: SingleInputDateRangeField }}
            slotProps={{
              field: {
                error: false,
                sx: {
                  flex: 1,
                  minWidth: 200,
                  '& .MuiPickersInputBase-root': {
                    backgroundColor: 'background.paperDark',
                  },
                },
              } as any,
            }}
          />
          <FormControl
            className="flex-1"
            sx={{ minWidth: 170, backgroundColor: 'background.paperDark' }}
          >
            <InputLabel id="sessions-status-label" shrink>
              Status
            </InputLabel>
            <Select
              labelId="sessions-status-label"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">
                <em>All Statuses</em>
              </MenuItem>
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(instructorsStatus === 'uninitialized' ||
            instructorsStatus === 'pending' ||
            eventInstructors.length > 1) &&
            (instructorsStatus === 'uninitialized' ||
            instructorsStatus === 'pending' ? (
              <Skeleton
                variant="rectangular"
                height={56}
                className="flex-1"
                sx={{ minWidth: 170, borderRadius: 1 }}
              />
            ) : (
              <FormControl
                className="flex-1"
                sx={{ minWidth: 170, backgroundColor: 'background.paperDark' }}
              >
                <InputLabel id="sessions-instructor-label" shrink>
                  Instructor
                </InputLabel>
                <Select
                  labelId="sessions-instructor-label"
                  label="Instructor"
                  value={instructorFilter}
                  onChange={(e) => setInstructorFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>All Instructors</em>
                  </MenuItem>
                  {eventInstructors.map((instructor) => (
                    <MenuItem key={instructor.id} value={instructor.name}>
                      {instructor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}
          {hasActiveFilters && (
            <Button
              size="small"
              variant="text"
              color="secondary"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              className="shrink-0 whitespace-nowrap"
            >
              Clear filters
            </Button>
          )}
        </Stack>
      </LocalizationProvider>

      {/* Session list */}
      {sessionsStatus === 'rejected' ? (
        <Box
          className="text-center rounded p-10"
          sx={{ background: '#1e2124', color: 'rgba(255,255,255,.5)' }}
        >
          Failed to load sessions. Please try again.
        </Box>
      ) : isLoading ? (
        <Box className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={120}
              className="rounded"
            />
          ))}
        </Box>
      ) : sessions.length === 0 ? (
        <Box
          className="text-center rounded p-10"
          sx={{ background: '#1e2124', color: 'rgba(255,255,255,.5)' }}
        >
          {hasActiveFilters
            ? 'No sessions match your filters.'
            : 'No sessions found for this event.'}
        </Box>
      ) : (
        <Box className="flex flex-col gap-3">
          {sessions.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              isVirtual={locationInfo.isVirtual}
              locationLabel={locationInfo.locationLabel}
              instructorLine={getInstructorLine(session)}
              onViewAttendees={goToAttendees}
              onEdit={handleEdit}
              onInvite={event?.linkedServiceID ? handleInvite : undefined}
            />
          ))}
        </Box>
      )}

      {/* Pagination footer */}
      {sessionsStatus !== 'rejected' && total > 0 && (
        <TablePagination
          total={total}
          page={page}
          pageSize={pageSize}
          onPageChange={(p) => {
            setPage(p)
            dispatch(
              fetchManageEventSessions({
                eventID,
                page: p,
                pageSize,
                q: debouncedSearch || undefined,
                instructor: instructorFilter || undefined,
                status: statusFilter || undefined,
                dateFrom: dateFrom?.format('YYYY-MM-DD') ?? undefined,
                dateTo: dateTo?.format('YYYY-MM-DD') ?? undefined,
              }),
            )
          }}
          onPageSizeChange={(s) => {
            setPageSize(s)
            setPage(1)
            dispatch(
              fetchManageEventSessions({
                eventID,
                page: 1,
                pageSize: s,
                q: debouncedSearch || undefined,
                instructor: instructorFilter || undefined,
                status: statusFilter || undefined,
                dateFrom: dateFrom?.format('YYYY-MM-DD') ?? undefined,
                dateTo: dateTo?.format('YYYY-MM-DD') ?? undefined,
              }),
            )
          }}
          itemLabel="sessions"
        />
      )}

      {inviteSession && event?.linkedServiceID && (
        <InviteUsersDialog
          open
          onClose={() => setInviteSession(null)}
          linkedServiceID={event.linkedServiceID}
          session={inviteSession}
          onSaved={handleInviteSaved}
        />
      )}
    </Box>
  )
}

export default SessionsTab
